import { Alert as AntAlert, Button, Descriptions, Drawer, Table, Tag, Typography } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { SeverityTag } from '@/oversight/badges';
import { formatRelative } from '@/lib/time';
import { ROUTE_FAILURE_KEY, routeFailure, type AlertView } from '@/api/alerts';
import { erasedOr } from '@/lib/erased';

const { Text } = Typography;

export interface AlertDetailDrawerProps {
  alert: AlertView | null;
  onClose: () => void;
  /** Jump to the routed work item in the Workbench — where the DISPOSITION happens. */
  onOpenWorkItem: (workItemId: string) => void;
  /** Jump to the entity the alert is about (entity-360). */
  onOpenEntity: (entityId: string) => void;
}

function Mono({ value }: { value: string }) {
  return (
    <Text className="ol-mono" style={{ fontSize: 11.5 }} copyable={{ tooltips: false }}>
      {value}
    </Text>
  );
}

/**
 * Alert detail (OPSFL-ALUI-1): the payload facts, the routing trail and the disposition state of
 * ONE alert. Deliberately read-only — an alert is WORKED in the Workbench (the routed work item);
 * this drawer shows state and routes there. A routing failure is an error banner at the top (I4),
 * with the failing rule named.
 */
export function AlertDetailDrawer({
  alert,
  onClose,
  onOpenWorkItem,
  onOpenEntity,
}: AlertDetailDrawerProps) {
  const failure = alert ? routeFailure(alert) : null;
  const now = Date.now();
  const facts = alert
    ? Object.entries(alert.detail).filter(([k]) => k !== ROUTE_FAILURE_KEY)
    : [];

  return (
    <Drawer
      open={!!alert}
      onClose={onClose}
      width={560}
      title={
        alert ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <SeverityTag severity={alert.severity} />
            <span>{alert.type}</span>
          </span>
        ) : null
      }
    >
      {alert && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {failure && (
            <AntAlert
              type="error"
              showIcon
              message="Routing failed — this alert needs a human"
              description={
                <>
                  <div>{failure}</div>
                  {alert.routedRuleId && (
                    <div style={{ marginTop: 4 }}>
                      Matched rule: <Mono value={alert.routedRuleId} />
                    </div>
                  )}
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    The alert stays OPEN, never silently dropped. Fix the rule (or publish the flow
                    it names) — intake is idempotent, the next matching alert will route.
                  </div>
                </>
              }
            />
          )}

          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              { key: 'key', label: 'Alert key', children: <Mono value={alert.alertKey} /> },
              { key: 'status', label: 'Status', children: <Tag>{alert.status}</Tag> },
              { key: 'source', label: 'Source', children: alert.source },
              {
                key: 'raised',
                label: 'Raised',
                children: (
                  <span title={alert.raisedAt ?? undefined}>
                    {formatRelative(alert.raisedAt, now)}
                  </span>
                ),
              },
              ...(alert.entityId
                ? [
                    {
                      key: 'entity',
                      label: 'Entity',
                      children: (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => onOpenEntity(alert.entityId!)}
                        >
                          {alert.entityId}
                        </Button>
                      ),
                    },
                  ]
                : []),
              ...(alert.signalId
                ? [
                    {
                      key: 'signal',
                      label: 'Source signal',
                      children: <Mono value={alert.signalId} />,
                    },
                  ]
                : []),
            ]}
          />

          <div>
            <Text strong style={{ fontSize: 12.5 }}>
              Payload facts
            </Text>
            <div style={{ marginTop: 6 }}>
              {facts.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No detail facts on this alert (the detail map carries curated scalars only —
                  never PII, never a correlation payload).
                </Text>
              ) : (
                <Table<[string, unknown]>
                  size="small"
                  rowKey={([k]) => k}
                  pagination={false}
                  showHeader={false}
                  columns={[
                    {
                      key: 'k',
                      width: 180,
                      render: (_, [k]) => <Mono value={k} />,
                    },
                    { key: 'v', render: (_, [, v]) => String(erasedOr(v)) },
                  ]}
                  dataSource={facts}
                />
              )}
            </div>
          </div>

          <div>
            <Text strong style={{ fontSize: 12.5 }}>
              Routing
            </Text>
            <Descriptions
              size="small"
              column={1}
              style={{ marginTop: 6 }}
              items={[
                {
                  key: 'rule',
                  label: 'Rule',
                  children: alert.routedRuleId ? (
                    <Mono value={alert.routedRuleId} />
                  ) : (
                    <Text type="secondary">no rule matched — the alert simply stays OPEN</Text>
                  ),
                },
                {
                  key: 'run',
                  label: 'Run',
                  children: alert.routedRunId ? (
                    <Mono value={alert.routedRunId} />
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
                },
                {
                  key: 'at',
                  label: 'Routed',
                  children: alert.routedAt ? (
                    <span title={alert.routedAt}>{formatRelative(alert.routedAt, now)}</span>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
                },
              ]}
            />
          </div>

          <div>
            <Text strong style={{ fontSize: 12.5 }}>
              Disposition
            </Text>
            <div style={{ marginTop: 6 }}>
              {alert.disposition ? (
                <Descriptions
                  size="small"
                  column={1}
                  items={[
                    { key: 'd', label: 'Disposition', children: <Tag>{alert.disposition}</Tag> },
                    { key: 'by', label: 'By', children: alert.disposedBy ?? '—' },
                    {
                      key: 'at',
                      label: 'At',
                      children: alert.disposedAt ? (
                        <span title={alert.disposedAt}>{formatRelative(alert.disposedAt, now)}</span>
                      ) : (
                        '—'
                      ),
                    },
                    ...(alert.dispositionNote
                      ? [{ key: 'note', label: 'Note', children: erasedOr(alert.dispositionNote) }]
                      : []),
                  ]}
                />
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Not dispositioned yet.{' '}
                  {alert.workItemId
                    ? 'Alerts are worked where the case is — open the work item below.'
                    : 'This alert has no routed work item yet; it is worked once a rule routes it.'}
                </Text>
              )}
            </div>
          </div>

          {alert.workItemId && (
            <Button
              type="primary"
              icon={<InboxOutlined />}
              onClick={() => onOpenWorkItem(alert.workItemId!)}
            >
              Open work item in Workbench
            </Button>
          )}
        </div>
      )}
    </Drawer>
  );
}
